import { useLazyQuery } from '@apollo/client';
import { useParams, useNavigate } from 'react-router-dom';
import { GET_USER_ACTIVITY } from '~/services';
import { GET_USER } from '~/services/user';
import { ActivityCard } from './ActivityCard';
import { useTranslation } from 'react-i18next';
import { ActivityCardComent } from './ActivityCardComent';
import { useState, useEffect } from 'react';
import ActivityDialog from './ActivityDialog';
import QuickReplyDialog from './QuickReplyDialog';
import { ProfileDialog } from './ProfileDialog';
import { useDispatch, useSelector } from 'react-redux';
import Skeleton from 'react-loading-skeleton';
import { motion, AnimatePresence } from 'motion/react';
import {
  addActivityRecords,
  addActivityRecord,
  selectActivityRecordsByActivityId,
  selectIsActivityLoaded,
  ActivityRecord,
} from '~/store/activityRecords';
import { RootState } from '~/store';

// Define the response type from the GET_USER_ACTIVITY query
interface UserActivityResponse {
  response: {
    data: Partial<ActivityRecord>[];
  };
}

// Animation variants for stacked card entrance
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15, // Stagger each card by 150ms
      delayChildren: 0.1, // Small initial delay
    },
  },
};

const cardVariants = {
  hidden: {
    opacity: 0,
    x: -80, // Start from the left (like coming from nav bar)
    scale: 0.9, // Slightly smaller initially
    rotateY: -15, // 3D rotation effect
  },
  visible: {
    opacity: 1,
    x: 0,
    scale: 1,
    rotateY: 0,
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 25,
      duration: 0.6,
    },
  },
  exit: {
    opacity: 0,
    x: -50,
    scale: 0.95,
    transition: {
      duration: 0.3,
    },
  },
};

// Button animations
const buttonVariants = {
  hidden: { opacity: 0, y: -20, scale: 0.9 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: 'spring',
      stiffness: 400,
      damping: 20,
    },
  },
};

// Loading skeleton animations
const skeletonVariants = {
  hidden: { opacity: 0, x: -30 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const skeletonItemVariants = {
  hidden: { opacity: 0, x: -30 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 20,
    },
  },
};

export const UserActivity = () => {
  const [t] = useTranslation('activity');
  const { personId, agentId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isQuickReplyDialogOpen, setIsQuickReplyDialogOpen] = useState(false);
  const [isProfileDialogOpen, setIsProfileDialogOpen] = useState(false);

  // Redux selectors
  const activityRecords = useSelector((state: RootState) =>
    personId && agentId
      ? selectActivityRecordsByActivityId(state, personId, agentId)
      : [],
  );
  const isActivityLoaded = useSelector((state: RootState) =>
    personId && agentId
      ? selectIsActivityLoaded(state, `${personId}-${agentId}`)
      : false,
  );

  const [query, { loading }] = useLazyQuery<UserActivityResponse>(
    GET_USER_ACTIVITY,
    {
      onCompleted: (data) => {
        if (!data?.response?.data || data.response.data.length === 0) {
          if (personId && agentId) {
            dispatch(
              addActivityRecords({
                records: [],
                activityId: `${personId}-${agentId}`,
              }),
            );
          }
          return;
        }

        if (personId && agentId) {
          dispatch(
            addActivityRecords({
              records: data.response.data,
              activityId: `${personId}-${agentId}`,
            }),
          );
        }
      },
      onError: () => {
        if (personId && agentId) {
          dispatch(
            addActivityRecords({
              records: [],
              activityId: `${personId}-${agentId}`,
            }),
          );
        }
      },
    },
  );

  const [getUserQuery, { data: userData }] = useLazyQuery(GET_USER, {});

  useEffect(() => {
     
    if (!personId || !agentId) return;

    if (!isActivityLoaded) {
       query({
        variables: {
          data: {
            user_id: personId,
            agent_id: agentId,
          },
        },
      });
    }

    // Load user information
    if (personId) {
      getUserQuery({
        variables: {
          data: {
            id: personId,
            channel_type: 'whatsapp,instagram',
          },
        },
      });
    }
  }, [personId, agentId, isActivityLoaded, query, getUserQuery]);

  const handleAddObservation = () => {
    setIsDialogOpen(true);
  };

  const handleOpenQuickReplyDialog = () => {
    setIsQuickReplyDialogOpen(true);
  };

  const handleOpenProfile = () => {
    // Open profile dialog to show user attributes
    setIsProfileDialogOpen(true);
  };

  const handleCommentUpdate = (updatedComment: any) => {
    const newActivity = updatedComment.data.create_comment.data;
    dispatch(addActivityRecord(newActivity as Partial<ActivityRecord>));
  };

  const shouldShowQuickReply = activityRecords
    .filter((a) => a.activity_type === 'CONVERSATIONS')
    .every((activity) => activity.ended_at);

  // Extract user name from user data
  const userName = userData?.get_user?.data?.profile_name || 
                   userData?.get_user?.data?.attributes?.first_name || 
                   undefined;
  
  return (
    <motion.div
      className="mx-auto flex h-[91vh] w-full max-w-[1024px] flex-col overflow-y-scroll px-8 py-12"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {/* Action Buttons with Animation */}
      <motion.div
        className="mb-4 flex w-full justify-end gap-2"
        variants={buttonVariants}
      >
        <AnimatePresence>
          {shouldShowQuickReply && (
            <motion.button
              className="btn btn-primary rounded-full"
              onClick={handleOpenQuickReplyDialog}
              initial={{ opacity: 0, scale: 0.8, x: 20 }}
              animate={{
                opacity: 1,
                scale: 1,
                x: 0,
                transition: {
                  type: 'spring',
                  stiffness: 400,
                  damping: 20,
                },
              }}
              exit={{
                opacity: 0,
                scale: 0.8,
                x: 20,
                transition: { duration: 0.2 },
              }}
              whileHover={{
                scale: 1.05,
                transition: { duration: 0.2 },
              }}
              whileTap={{ scale: 0.95 }}
            >
              {t('send_quick_reply')}
            </motion.button>
          )}
        </AnimatePresence>
        <motion.button
          className="btn btn-outline rounded-full"
          onClick={handleOpenProfile}
          whileHover={{
            scale: 1.05,
            transition: { duration: 0.2 },
          }}
          whileTap={{ scale: 0.95 }}
        >
          {t('profile', { defaultValue: 'Perfil' })}
        </motion.button>

        <motion.button
          className="btn btn-secondary rounded-full"
          onClick={handleAddObservation}
          whileHover={{
            scale: 1.05,
            transition: { duration: 0.2 },
          }}
          whileTap={{ scale: 0.95 }}
        >
          {t('add_observation')}
        </motion.button>
      </motion.div>

      {/* Loading State with Staggered Animation */}
      {loading && (
        <motion.div
          className="space-y-4"
          variants={skeletonVariants}
          initial="hidden"
          animate="visible"
        >
          {[...Array(3)].map((_, index) => (
            <motion.div key={index} variants={skeletonItemVariants}>
              <Skeleton height={80} />
            </motion.div>
          ))}
        </motion.div>
      )}

      {/* Empty State with Animation */}
      {!loading && activityRecords.length === 0 && (
        <motion.div
          className="mt-10 text-center text-gray-500"
          initial={{ opacity: 0, y: 20 }}
          animate={{
            opacity: 1,
            y: 0,
            transition: {
              delay: 0.3,
              type: 'spring',
              stiffness: 300,
              damping: 25,
            },
          }}
        >
          {t('no_activity_found')}
        </motion.div>
      )}

      {/* Activity Cards with Stacked Entrance Animation */}
      <AnimatePresence mode="popLayout">
        {!loading &&
          activityRecords.map((activity, index) => (
            <motion.div
              key={activity._id}
              variants={cardVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              layout
              custom={index}
              style={{
                // Add slight perspective for 3D effect
                transformStyle: 'preserve-3d',
              }}
              whileHover={{
                scale: 1.02,
                x: 8,
                transition: {
                  type: 'spring',
                  stiffness: 400,
                  damping: 25,
                },
              }}
            >
              {activity.activity_type === 'COMMENTS' ? (
                <ActivityCardComent
                  key={activity._id}
                  activity={activity as any}
                />
              ) : (
                <ActivityCard
                  key={activity._id}
                  activity={activity}
                  hasOpenConversation={!shouldShowQuickReply}
                />
              )}
            </motion.div>
          ))}
      </AnimatePresence>

      {/* Dialogs */}
      {isDialogOpen && personId && agentId && (
        <ActivityDialog
          onClose={() => setIsDialogOpen(false)}
          open={isDialogOpen}
          activity={{ person_id: personId, agent_id: agentId }}
          type="add"
          onCommentUpdate={handleCommentUpdate}
        />
      )}

      {isQuickReplyDialogOpen &&
        personId &&
        agentId &&
        activityRecords.length > 0 && (
          <QuickReplyDialog
            personChannelId={activityRecords[0].person_channel_id || ''}
            open={isQuickReplyDialogOpen}
            onClose={() => setIsQuickReplyDialogOpen(false)}
            personId={personId}
            agentId={agentId}
            channelType="whatsapp"
          />
        )}

      {isProfileDialogOpen && personId && agentId && (
        <ProfileDialog
          isOpen={isProfileDialogOpen}
          onClose={() => setIsProfileDialogOpen(false)}
          personId={personId}
          agentId={agentId}
          userName={userName}
        />
      )}
    </motion.div>
  );
};
